import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  compute,
  money,
  formatDate,
  parseDate,
  addDays,
  formatDateInput,
  FEE_COUNT,
  DEFAULT_SERVICE_CONTRACT_FEE,
  DEFAULT_APR,
  OHIO_MAX_APR,
  realizedProfit,
  portfolioCashPositiveReport,
  formatAprPercent,
} from './bhphMath';
import {
  printBhphPackage,
  buildCustomerStatementHtml,
  buildInternalPnlHtml,
  buildPrintHtml,
  buildPaymentReceiptHtml,
} from './printPackage';
import BhphDispositionPanel from './BhphDispositionPanel';
import { takeBhphPrefill } from './mapDealToBhphPrefill';

const LATE_REASONS = [
  { value: 'card_decline', label: 'Card decline' },
  { value: 'nsf', label: 'NSF / returned' },
  { value: 'no_funds', label: 'No funds / insufficient' },
  { value: 'customer_request', label: 'Customer request' },
  { value: 'lost_job', label: 'Lost job / hardship' },
  { value: 'other', label: 'Other' },
];

const LATE_SOLUTIONS = [
  { value: 'paid_late_fee', label: 'Paid late fee' },
  { value: 'waived', label: 'Waived' },
  { value: 'rearranged', label: 'Rearranged date' },
  { value: 'partial_paid', label: 'Partial paid' },
  { value: 'repossession', label: 'Repossession path' },
  { value: 'other', label: 'Other' },
];

const DEFAULT_SELLER_EMAIL = 'titles@thewholesalecars.com';

const emptyForm = () => {
  const fees = {};
  for (let i = 1; i <= FEE_COUNT; i++) {
    fees[`fee${i}Label`] = '';
    fees[`fee${i}Amt`] = '';
  }
  return {
    sourceDealId: null,
    buyerName: '',
    buyerDob: '',
    buyerStreet: '',
    buyerCity: '',
    buyerState: 'OH',
    buyerZip: '',
    buyerPhone: '',
    buyerPhoneAlt: '',
    buyerEmail: '',
    buyerDl: '',
    buyerDlState: '',
    contactEmail: true,
    contactSms: true,
    contactVoice: false,
    consentSms: true,
    emergencyName: '',
    emergencyPhone: '',
    coBuyerName: '',
    coBuyerPhone: '',
    coBuyerEmail: '',
    vehYear: '',
    vehMake: '',
    vehModel: '',
    vehColor: '',
    vehVin: '',
    vehStock: '',
    vehOdometer: '',
    saleDate: formatDateInput(new Date()),
    cashPrice: '',
    ...fees,
    tradeDesc: '',
    tradeAmt: '',
    downPayment: '',
    taxTitleAmt: '',
    taxTitleDate: '',
    firstDue: '',
    payAmount: '',
    numPayments: '26',
    apr: String(DEFAULT_APR * 100),
    lateFee: '35',
    declineFee: '10',
    repoFee: '199',
    lateFeeStartDays: '5',
    lateFeeRepeatDays: '3',
    impoundFee: '299',
    keyTurninBonus: '100',
    serviceContract: false,
    serviceContractFee: String(DEFAULT_SERVICE_CONTRACT_FEE),
    costInCar: '',
    gpsInstalled: false,
    gpsSerial: '',
    cardName: '',
    cardBrand: '',
    cardLast4: '',
    cardExp: '',
    cardZip: '',
    notes: '',
  };
};

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-medium text-neutral-500 mb-1">{label}</span>
    {children}
  </label>
);

const inputCls = 'w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white';

function delinquencyBadge(d, status) {
  if (status === 'paid_off') return <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">Paid off</span>;
  if (status === 'defaulted') return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800">Defaulted</span>;
  if (status === 'repossessed') return <span className="px-2 py-0.5 text-xs rounded bg-red-200 text-red-900">Repo</span>;
  const bucket = d?.bucket || 'current';
  if (bucket === 'paid') return <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">Current / paid</span>;
  if (bucket === '10plus') return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800">{d.daysPastDue}d past due</span>;
  if (bucket === '1to9') return <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">{d.daysPastDue}d past due</span>;
  return <span className="px-2 py-0.5 text-xs rounded bg-neutral-100 text-neutral-700">Current</span>;
}

function DetailRow({ label, value }) {
  return (
    <div className="flex gap-2 text-sm py-0.5">
      <span className="text-neutral-500 w-40 shrink-0">{label}</span>
      <span className="text-neutral-900 break-words">{value || '-'}</span>
    </div>
  );
}

function accountToForm(account, company) {
  const snap = account.form_snapshot || {};
  const fees = account.fees_json || [];
  const form = emptyForm();
  Object.assign(form, snap);
  form.sourceDealId = account.source_deal_id;
  form.buyerName = account.buyer_name || '';
  form.buyerDob = account.buyer_dob ? String(account.buyer_dob).slice(0, 10) : '';
  form.buyerStreet = account.buyer_street || '';
  form.buyerCity = account.buyer_city || '';
  form.buyerState = account.buyer_state || 'OH';
  form.buyerZip = account.buyer_zip || '';
  form.buyerPhone = account.buyer_phone || '';
  form.buyerPhoneAlt = account.buyer_phone_alt || '';
  form.buyerEmail = account.buyer_email || '';
  form.buyerDl = account.buyer_dl || '';
  form.buyerDlState = account.buyer_dl_state || '';
  form.coBuyerName = account.co_buyer_name || '';
  form.coBuyerPhone = account.co_buyer_phone || '';
  form.coBuyerEmail = account.co_buyer_email || '';
  form.vehYear = account.veh_year || '';
  form.vehMake = account.veh_make || '';
  form.vehModel = account.veh_model || '';
  form.vehColor = account.veh_color || '';
  form.vehVin = account.veh_vin || '';
  form.vehStock = account.veh_stock || '';
  form.vehOdometer = account.veh_odometer || '';
  form.saleDate = account.sale_date ? String(account.sale_date).slice(0, 10) : '';
  form.cashPrice = account.cash_price != null ? String(account.cash_price) : '';
  form.tradeAmt = account.trade_amt != null ? String(account.trade_amt) : '';
  form.tradeDesc = account.trade_desc || '';
  form.downPayment = account.down_payment != null ? String(account.down_payment) : '';
  form.taxTitleAmt = account.tax_title_amt != null ? String(account.tax_title_amt) : '';
  form.taxTitleDate = account.tax_title_date ? String(account.tax_title_date).slice(0, 10) : '';
  form.firstDue = account.first_due ? String(account.first_due).slice(0, 10) : '';
  form.payAmount = account.pay_amount != null ? String(account.pay_amount) : '';
  form.numPayments = account.num_payments != null ? String(account.num_payments) : '';
  {
    const rawApr = account.apr != null ? Number(account.apr) : Number(company?.default_apr ?? DEFAULT_APR);
    const pct = rawApr > 1 ? rawApr : rawApr * 100;
    form.apr = String(Math.round(pct * 100) / 100);
  }
  form.lateFee = String(account.late_fee ?? company?.late_fee ?? 35);
  form.declineFee = String(account.decline_fee ?? company?.decline_fee ?? 10);
  form.repoFee = String(account.repo_fee ?? company?.repo_fee ?? 199);
  form.lateFeeStartDays = String(account.late_fee_start_days ?? company?.late_fee_start_days ?? 5);
  form.lateFeeRepeatDays = String(account.late_fee_repeat_days ?? company?.late_fee_repeat_days ?? 3);
  form.impoundFee = String(account.impound_admin_fee ?? company?.impound_admin_fee ?? 299);
  form.keyTurninBonus = String(account.key_turnin_bonus ?? company?.key_turnin_bonus ?? 100);
  form.serviceContract = !!account.service_contract;
  form.serviceContractFee = String(account.service_contract_fee ?? company?.service_contract_fee ?? DEFAULT_SERVICE_CONTRACT_FEE);
  form.costInCar = account.cost_in_car != null ? String(account.cost_in_car) : '';
  form.gpsInstalled = !!account.gps_installed;
  form.gpsSerial = account.gps_serial || '';
  form.cardName = account.card_name || '';
  form.cardBrand = account.card_brand || '';
  form.cardLast4 = account.card_last4 || '';
  form.cardExp = account.card_exp || '';
  form.cardZip = account.card_zip || '';
  form.notes = account.notes || '';
  fees.forEach((f, i) => {
    if (i < 6) {
      form[`fee${i + 1}Label`] = f.label || '';
      form[`fee${i + 1}Amt`] = f.amount != null ? String(f.amount) : '';
    }
  });
  return form;
}

export default function BhphPortfolio() {
  const [tab, setTab] = useState('portfolio');
  const [company, setCompany] = useState(null);
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [profitByCustomer, setProfitByCustomer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [companyForm, setCompanyForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [payForm, setPayForm] = useState({
    amount: '',
    paid_on: formatDateInput(new Date()),
    kind: 'installment',
    method: 'card_draft',
    notes: '',
  });
  const [message, setMessage] = useState('');
  const [showRepo, setShowRepo] = useState(false);
  const [lateForm, setLateForm] = useState({
    event_on: formatDateInput(new Date()),
    reason: 'card_decline',
    solution: 'paid_late_fee',
    notes: '',
  });

  const calc = useMemo(() => compute(form), [form]);
  const cashReport = useMemo(() => portfolioCashPositiveReport(accounts), [accounts]);

  const load = async () => {
    setLoading(true);
    try {
      const [co, sum, list, profit] = await Promise.all([
        api.getBhphCompany().catch(() => null),
        api.getBhphSummary().catch(() => null),
        api.getBhphAccounts().catch(() => []),
        api.getBhphProfitabilityByCustomer().catch(() => []),
      ]);
      setCompany(co);
      setSummary(sum);
      setAccounts(list || []);
      setProfitByCustomer(profit || []);
      if (co) {
        setCompanyForm({
          legal_name: co.legal_name || '',
          dba: co.dba || '',
          street: co.street || '',
          city: co.city || '',
          state: co.state || 'OH',
          zip: co.zip || '',
          phone: co.phone || '',
          email: co.email || DEFAULT_SELLER_EMAIL,
          alert_email: co.alert_email || DEFAULT_SELLER_EMAIL,
          website: co.website || '',
          dealer_number: co.dealer_number || '',
          county: co.county || 'Lucas',
          late_fee: co.late_fee ?? 35,
          decline_fee: co.decline_fee ?? 10,
          repo_fee: co.repo_fee ?? 199,
          default_repo_expense: co.default_repo_expense ?? 640,
          storage_per_day: co.storage_per_day ?? 20,
          late_fee_start_days: co.late_fee_start_days ?? 5,
          late_fee_repeat_days: co.late_fee_repeat_days ?? 3,
          impound_admin_fee: co.impound_admin_fee ?? 299,
          key_turnin_bonus: co.key_turnin_bonus ?? 100,
          service_contract_fee: co.service_contract_fee ?? DEFAULT_SERVICE_CONTRACT_FEE,
          repo_company_name: co.repo_company_name || '',
          repo_company_phone: co.repo_company_phone || '',
          repo_company_address: co.repo_company_address || '',
          default_apr:
            co.default_apr != null
              ? String(
                  Math.round(
                    (Number(co.default_apr) > 1
                      ? Number(co.default_apr)
                      : Number(co.default_apr) * 100) * 100
                  ) / 100
                )
              : String(DEFAULT_APR * 100),
        });
      } else {
        setCompanyForm({
          email: DEFAULT_SELLER_EMAIL,
          alert_email: DEFAULT_SELLER_EMAIL,
          state: 'OH',
          county: 'Lucas',
          late_fee: 35,
          decline_fee: 10,
          repo_fee: 199,
          default_repo_expense: 640,
          storage_per_day: 20,
          late_fee_start_days: 5,
          late_fee_repeat_days: 3,
          impound_admin_fee: 299,
          key_turnin_bonus: 100,
          service_contract_fee: DEFAULT_SERVICE_CONTRACT_FEE,
          default_apr: String(DEFAULT_APR * 100),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const prefill = takeBhphPrefill();
    if (prefill) {
      setForm((f) => ({ ...f, ...prefill, declineFee: prefill.declineFee || '10' }));
      setTab('new');
      setMessage(
        prefill.sourceDealId
          ? `Prefill from Deal #${prefill.sourceDealId}. Finish schedule and card, then save.`
          : 'Prefill loaded.'
      );
    }
  }, []);

  useEffect(() => {
    const sale = parseDate(form.saleDate);
    if (!sale) return;
    // New accounts: always keep first due = sale+14 and Tax/Title/License = first+14.
    // Editing: only fill blanks so saved overrides are kept.
    setForm((f) => {
      if (editingId) {
        const next = { ...f };
        if (!f.firstDue) next.firstDue = formatDateInput(addDays(sale, 14));
        const first = parseDate(next.firstDue || f.firstDue);
        if (first && !f.taxTitleDate) next.taxTitleDate = formatDateInput(addDays(first, 14));
        return next;
      }
      const firstDue = formatDateInput(addDays(sale, 14));
      return {
        ...f,
        firstDue,
        taxTitleDate: formatDateInput(addDays(parseDate(firstDue), 14)),
      };
    });
  }, [form.saleDate, editingId]);

  const reloadDetail = async (id) => {
    const d = await api.getBhphAccount(id);
    setDetail(d);
    return d;
  };

  const openAccount = async (id) => {
    setEditingId(null);
    setShowRepo(false);
    try {
      await reloadDetail(id);
      setTab('detail');
    } catch (e) {
      alert(e.message || 'Could not load account');
    }
  };

  const persistDisposition = async (patch) => {
    if (!detail?.id) return;
    const body = { ...patch };
    Object.keys(body).forEach((k) => {
      if (body[k] === undefined) delete body[k];
    });
    await api.updateBhphAccount(detail.id, body);
    await reloadDetail(detail.id);
    await load();
  };

  const startEdit = async (id) => {
    try {
      const d = await api.getBhphAccount(id);
      setDetail(d);
      setEditingId(id);
      setForm(accountToForm(d, company));
      setTab('edit');
    } catch (e) {
      alert(e.message || 'Could not load account');
    }
  };

  const set = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const saveAccount = async () => {
    if (!form.buyerName || !form.vehVin) {
      alert('Buyer name and VIN are required.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const updated = await api.updateBhphAccount(editingId, { ...form, fullUpdate: true });
        setMessage(`Account #${updated.id} updated.`);
        setEditingId(null);
        setForm(emptyForm());
        await load();
        await openAccount(updated.id);
      } else {
        const created = await api.createBhphAccount(form);
        setMessage(`Account #${created.id} created.`);
        setForm(emptyForm());
        await load();
        await openAccount(created.id);
      }
    } catch (e) {
      alert(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveCompany = async () => {
    setSaving(true);
    try {
      const co = await api.saveBhphCompany(companyForm);
      setCompany(co);
      setMessage('Company settings saved.');
    } catch (e) {
      alert(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const printBhphReceipt = (account, payment) => {
    if (!account || !payment || payment.voided_at) return null;
    const receiptHtml = buildPaymentReceiptHtml(company, account, payment);
    const w = window.open('', '_blank');
    if (!w) {
      alert('Pop-up blocked. Allow pop-ups, then click Receipt again.');
      return receiptHtml;
    }
    w.document.write(receiptHtml);
    w.document.close();
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (_) {}
    }, 200);
    return receiptHtml;
  };

  const reprintPaymentReceipt = (payment) => {
    printBhphReceipt(detail, payment);
  };

  const postPayment = async () => {
    if (!detail?.id) return;
    setSaving(true);
    try {
      const payment = await api.createBhphPayment(detail.id, payForm);
      const fresh = await api.getBhphAccount(detail.id).catch(() => detail);
      const receiptHtml = printBhphReceipt(fresh, payment);
      await api.saveBhphDocument(detail.id, {
        doc_type: 'payment_receipt',
        title: `Payment receipt #${payment.id}`,
        html: receiptHtml,
        meta: {
          paymentId: payment.id,
          amount: payment.amount,
          paid_on: payment.paid_on,
          kind: payment.kind,
        },
      });
      setPayForm({
        amount: '',
        paid_on: formatDateInput(new Date()),
        kind: 'installment',
        method: 'card_draft',
        notes: '',
      });
      setMessage(`Payment posted · receipt saved (#${payment.id}).`);
      await openAccount(detail.id);
      await load();
    } catch (e) {
      alert(e.message || 'Payment failed');
    } finally {
      setSaving(false);
    }
  };

  const postLateEvent = async () => {
    if (!detail?.id) return;
    setSaving(true);
    try {
      await api.createBhphLateEvent(detail.id, lateForm);
      setLateForm({
        event_on: formatDateInput(new Date()),
        reason: 'card_decline',
        solution: 'paid_late_fee',
        notes: '',
      });
      setMessage('Late event logged.');
      await openAccount(detail.id);
    } catch (e) {
      alert(e.message || 'Could not save late event');
    } finally {
      setSaving(false);
    }
  };

  const removeLateEvent = async (id) => {
    if (!window.confirm('Remove this late history row?')) return;
    await api.deleteBhphLateEvent(id);
    await openAccount(detail.id);
  };

  const loadSamplePortfolio = async () => {
    const ok = window.confirm(
      'Load ~40 sample BHPH accounts (strong / solid / thin / bad / repo mix)?\n\nOK = replace prior sample accounts (BHPH-* stock only; your real accounts stay)\nCancel = abort'
    );
    if (!ok) return;
    setSaving(true);
    try {
      const r = await api.loadBhphSamplePortfolio(true);
      const tiers = r.tiers
        ? ` · strong ${r.tiers.strong || 0}, solid ${r.tiers.solid || 0}, thin ${r.tiers.thin || 0}, bad ${r.tiers.bad || 0}, repo ${r.tiers.repo || 0}`
        : '';
      setMessage(`Sample loaded: ${r.createdCount} accounts${tiers}.`);
      await load();
      if (r.richAccountId) await openAccount(r.richAccountId);
    } catch (e) {
      alert(e.message || 'Sample load failed');
    } finally {
      setSaving(false);
    }
  };

  const voidPayment = async (pid) => {
    if (!window.confirm('Void this BHPH payment? (Does not affect LotSense Accounting.)')) return;
    await api.voidBhphPayment(pid);
    await openAccount(detail.id);
    await load();
  };

  const updateStatus = async (status) => {
    await api.updateBhphAccount(detail.id, { status });
    await openAccount(detail.id);
    await load();
  };

  const saveDoc = async (doc_type, title, html, meta = {}) => {
    await api.saveBhphDocument(detail.id, { doc_type, title, html, meta });
    setMessage(`${title} saved to database.`);
    await openAccount(detail.id);
  };

  const handleCustomerStatement = async () => {
    const html = buildCustomerStatementHtml(company, detail, detail.delinquency);
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 200);
    }
    await saveDoc('customer_statement', 'Customer Statement', html, {
      remaining: detail.delinquency?.remainingPrincipal,
    });
  };

  const handleInternalPnl = async () => {
    const html = buildInternalPnlHtml(
      company,
      detail,
      detail.delinquency,
      detail.cost_in_car
    );
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 200);
    }
    await saveDoc('internal_pnl', 'Internal P&L', html, {
      cost: detail.cost_in_car,
      collected: detail.delinquency?.collected,
      profit: realizedProfit(detail.cost_in_car, detail.delinquency?.collected),
    });
  };

  const handlePrintContract = async () => {
    const f = accountToForm(detail, company);
    // Prefer live account APR/schedule fields over stale form_snapshot
    if (detail.apr != null) {
      const raw = Number(detail.apr);
      f.apr = String(Math.round((raw > 1 ? raw : raw * 100) * 100) / 100);
    }
    if (detail.num_payments != null) f.numPayments = String(detail.num_payments);
    if (detail.pay_amount != null) f.payAmount = String(detail.pay_amount);
    printBhphPackage(company, f);
    const built = buildPrintHtml(company, f);
    if (built?.html) {
      await saveDoc('contract_package_meta', 'Contract package printed', built.html, {
        printedAt: new Date().toISOString(),
        apr: built.compute?.apr,
        financeCharge: built.compute?.financeCharge,
      });
    }
  };

  const filtered = accounts
    .filter((a) => {
      if (filter === 'delinquent') return (a.delinquency?.daysPastDue || 0) >= 1 && a.status === 'active';
      if (filter === '10plus') return a.delinquency?.bucket === '10plus';
      if (filter !== 'all') return a.status === filter;
      return true;
    })
    .filter((a) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (a.buyer_name || '').toLowerCase().includes(s) ||
        (a.veh_vin || '').toLowerCase().includes(s) ||
        (a.veh_stock || '').toLowerCase().includes(s)
      );
    });

  const tabs = [
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'new', label: 'New Account' },
    { id: 'settings', label: 'Settings' },
  ];

  const detailProfit = detail
    ? realizedProfit(detail.cost_in_car, detail.delinquency?.collected)
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">BHPH Portfolio</h1>
          <p className="text-sm text-neutral-500">
            Ohio / Lucas County retail installment - contracts, servicing, P&amp;L. Siloed from Accounting.
          </p>
        </div>
        <div className="flex gap-1 bg-neutral-100 p-1 rounded-lg">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setEditingId(null);
                if (t.id === 'new') {
                  const f = emptyForm();
                  if (companyForm.default_apr != null && companyForm.default_apr !== '') {
                    f.apr = String(companyForm.default_apr);
                  }
                  setForm(f);
                }
              }}
              className={`px-3 py-1.5 text-sm rounded-md ${
                tab === t.id || ((tab === 'detail' || tab === 'edit') && t.id === 'portfolio')
                  ? 'bg-white shadow text-neutral-900'
                  : 'text-neutral-600'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className="text-sm bg-sky-50 text-sky-900 border border-sky-200 rounded-lg px-3 py-2 flex justify-between gap-2">
          <span>{message}</span>
          <button type="button" className="text-sky-700" onClick={() => setMessage('')}>
            Dismiss
          </button>
        </div>
      )}

      {summary && tab === 'portfolio' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ['Accounts', summary.totalAccounts],
              ['Active', summary.activeAccounts],
              ['Outstanding', money(summary.outstandingPrincipal)],
              ['Collected', money(summary.totalCollected)],
              ['Realized P/L', money(summary.realizedProfit)],
            ].map(([label, val]) => (
              <div key={label} className="bg-white border border-neutral-200 rounded-lg p-3">
                <div className="text-xs text-neutral-500">{label}</div>
                <div className="text-lg font-semibold mt-1">{val}</div>
              </div>
            ))}
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-3">
            <div className="text-sm font-medium mb-2">Cash-positive report</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-neutral-500">Cash positive</div>
                <div className="text-lg font-semibold text-emerald-700">
                  {cashReport.counts.cashPositive} / {cashReport.counts.total}
                </div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Not yet (active)</div>
                <div className="text-lg font-semibold text-amber-800">
                  {cashReport.notYet.length}
                </div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Gap to recover cost</div>
                <div className="text-lg font-semibold">
                  {money(cashReport.totals.gapToPortfolioCashPositive)}
                </div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Avg weeks to cash+</div>
                <div className="text-lg font-semibold">
                  {cashReport.totals.avgWeeksToCashPositive != null
                    ? cashReport.totals.avgWeeksToCashPositive
                    : '-'}
                </div>
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-2">
              Cash positive = BHPH cash collected ≥ cost in car. Time estimate uses remaining on-time
              biweekly installments at the contracted payment amount.
            </p>
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-neutral-500">Loading...</div>}

      {tab === 'portfolio' && !loading && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={loadSamplePortfolio}
              className="px-3 py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              Load sample portfolio (~40)
            </button>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-neutral-100 flex flex-wrap gap-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search buyer, VIN, stock..."
                className={`${inputCls} max-w-xs`}
              />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className={inputCls + ' max-w-[11rem]'}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="delinquent">Any past due</option>
                <option value="10plus">5+ days late</option>
                <option value="paid_off">Paid off</option>
                <option value="defaulted">Defaulted</option>
                <option value="repossessed">Repo</option>
              </select>
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm());
                  setTab('new');
                }}
                className="ml-auto px-3 py-1.5 text-sm bg-neutral-900 text-white rounded-lg"
              >
                New Account
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left text-xs text-neutral-500">
                  <tr>
                    <th className="px-3 py-2">Buyer</th>
                    <th className="px-3 py-2">Vehicle</th>
                    <th className="px-3 py-2">Remaining</th>
                    <th className="px-3 py-2">Collected</th>
                    <th className="px-3 py-2">Cost</th>
                    <th className="px-3 py-2">P/L</th>
                    <th className="px-3 py-2">Cash+</th>
                    <th className="px-3 py-2">To cash+</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    const pl = realizedProfit(a.cost_in_car, a.delinquency?.collected);
                    const cp = cashReport.rows.find((r) => r.id === a.id);
                    return (
                      <tr
                        key={a.id}
                        className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                        onClick={() => openAccount(a.id)}
                      >
                        <td className="px-3 py-2 font-medium">{a.buyer_name || '-'}</td>
                        <td className="px-3 py-2 text-neutral-600">
                          {[a.veh_year, a.veh_make, a.veh_model].filter(Boolean).join(' ')}
                          <div className="text-xs text-neutral-400 font-mono">{a.veh_vin}</div>
                        </td>
                        <td className="px-3 py-2">
                          {money(a.delinquency?.remainingPrincipal ?? a.unpaid_balance)}
                        </td>
                        <td className="px-3 py-2">{money(a.delinquency?.collected)}</td>
                        <td className="px-3 py-2">{money(a.cost_in_car)}</td>
                        <td className={`px-3 py-2 ${pl < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {money(pl)}
                        </td>
                        <td className="px-3 py-2">
                          {cp?.isCashPositive ? (
                            <span className="text-emerald-700 text-xs font-medium">Yes</span>
                          ) : (
                            <span className="text-amber-800 text-xs">No</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-neutral-600">
                          {cp?.isCashPositive
                            ? '-'
                            : cp?.periodsNeeded
                              ? `${cp.periodsNeeded} pays / ~${cp.weeksNeeded}w`
                              : '-'}
                        </td>
                        <td className="px-3 py-2">{delinquencyBadge(a.delinquency, a.status)}</td>
                        <td
                          className="px-3 py-2 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="text-xs text-sky-700 hover:underline mr-3"
                            onClick={() => openAccount(a.id)}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="text-xs text-neutral-800 hover:underline"
                            onClick={() => startEdit(a.id)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!filtered.length && (
                    <tr>
                      <td colSpan={10} className="px-3 py-8 text-center text-neutral-400">
                        No BHPH accounts yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b text-sm font-medium text-emerald-900 bg-emerald-50/50">
                Cash-positive accounts ({cashReport.cashPositive.length})
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-xs text-neutral-500 text-left sticky top-0">
                    <tr>
                      <th className="px-3 py-2">Buyer</th>
                      <th className="px-3 py-2">Collected</th>
                      <th className="px-3 py-2">Cost</th>
                      <th className="px-3 py-2">P/L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashReport.cashPositive.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t cursor-pointer hover:bg-neutral-50"
                        onClick={() => openAccount(r.id)}
                      >
                        <td className="px-3 py-2 font-medium">{r.buyer}</td>
                        <td className="px-3 py-2">{money(r.collected)}</td>
                        <td className="px-3 py-2">{money(r.cost)}</td>
                        <td className="px-3 py-2 text-emerald-700">{money(r.profit)}</td>
                      </tr>
                    ))}
                    {!cashReport.cashPositive.length && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-neutral-400">
                          None yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b text-sm font-medium text-amber-950 bg-amber-50/50">
                Not yet cash-positive ({cashReport.notYet.length})
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-xs text-neutral-500 text-left sticky top-0">
                    <tr>
                      <th className="px-3 py-2">Buyer</th>
                      <th className="px-3 py-2">Gap</th>
                      <th className="px-3 py-2">Est. pays</th>
                      <th className="px-3 py-2">Est. date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashReport.notYet.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t cursor-pointer hover:bg-neutral-50"
                        onClick={() => openAccount(r.id)}
                      >
                        <td className="px-3 py-2 font-medium">{r.buyer}</td>
                        <td className="px-3 py-2">{money(r.gap)}</td>
                        <td className="px-3 py-2">
                          {r.periodsNeeded ? `${r.periodsNeeded} (~${r.weeksNeeded}w)` : '-'}
                        </td>
                        <td className="px-3 py-2 text-xs">{r.estimatedDate || '-'}</td>
                      </tr>
                    ))}
                    {!cashReport.notYet.length && (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-neutral-400">
                          All tracked accounts are cash-positive.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b text-sm font-medium">Profitability by customer (realized)</div>
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500 text-left">
                <tr>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Accounts</th>
                  <th className="px-3 py-2">Cost in cars</th>
                  <th className="px-3 py-2">Collected</th>
                  <th className="px-3 py-2">Realized P/L</th>
                </tr>
              </thead>
              <tbody>
                {(profitByCustomer || []).map((row) => (
                  <tr key={row.buyer_name} className="border-t">
                    <td className="px-3 py-2 font-medium">{row.buyer_name || 'Unknown'}</td>
                    <td className="px-3 py-2">{row.accountCount ?? row.account_count}</td>
                    <td className="px-3 py-2">{money(row.totalCost ?? row.total_cost)}</td>
                    <td className="px-3 py-2">{money(row.totalCollected ?? row.total_collected)}</td>
                    <td className="px-3 py-2">
                      {money(row.realizedProfit ?? row.realized_profit)}
                    </td>
                  </tr>
                ))}
                {!(profitByCustomer || []).length && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-neutral-400">
                      No profitability data yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'detail' && detail && (
        <div className="space-y-4">
          {!(Number(detail.apr) > 0) && (
            <div className="text-sm bg-amber-50 text-amber-950 border border-amber-200 rounded-lg px-3 py-2">
              This account is at <strong>0% APR</strong> - Truth in Lending will show $0 finance charge.
              Edit account → set APR (e.g. 18) and # of biweekly payments → Save, then print.
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={() => setTab('portfolio')}
              className="text-sm text-neutral-600 hover:underline"
            >
              ← Portfolio
            </button>
            <h2 className="text-lg font-semibold">
              #{detail.id} · {detail.buyer_name}
            </h2>
            {delinquencyBadge(detail.delinquency, detail.status)}
            <div className="ml-auto flex flex-wrap gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm bg-neutral-900 text-white rounded-lg"
                onClick={() => startEdit(detail.id)}
              >
                Edit account
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded-lg"
                onClick={handlePrintContract}
              >
                Print package (TIL + contract)
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded-lg"
                onClick={handleCustomerStatement}
              >
                Customer statement
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded-lg"
                onClick={handleInternalPnl}
              >
                Internal P&amp;L
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm border border-red-300 text-red-800 rounded-lg"
                onClick={() => setShowRepo(true)}
              >
                Repo / disposition
              </button>
              {detail.statutory_cure_used && (
                <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-950 border border-amber-200">
                  1317.12 cure used
                </span>
              )}
              <select
                className={inputCls + ' w-auto'}
                value={detail.status}
                onChange={(e) => updateStatus(e.target.value)}
              >
                <option value="active">Active</option>
                <option value="paid_off">Paid off</option>
                <option value="defaulted">Defaulted</option>
                <option value="repossessed">Repossessed</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <div className="bg-white border rounded-lg p-3">
              <div className="text-xs text-neutral-500">Remaining principal</div>
              <div className="text-xl font-semibold">
                {money(detail.delinquency?.remainingPrincipal)}
              </div>
            </div>
            <div className="bg-white border rounded-lg p-3">
              <div className="text-xs text-neutral-500">Cost in car (editable on Edit)</div>
              <div className="text-xl font-semibold">{money(detail.cost_in_car)}</div>
            </div>
            <div className="bg-white border rounded-lg p-3">
              <div className="text-xs text-neutral-500">Cash collected</div>
              <div className="text-xl font-semibold">{money(detail.delinquency?.collected)}</div>
            </div>
            <div className="bg-white border rounded-lg p-3">
              <div className="text-xs text-neutral-500">Realized P/L</div>
              <div
                className={`text-xl font-semibold ${
                  detailProfit < 0 ? 'text-red-700' : 'text-emerald-700'
                }`}
              >
                {money(detailProfit)}
              </div>
            </div>
          </div>

          {showRepo && (
            <BhphDispositionPanel
              company={company}
              account={detail}
              saving={saving}
              onClose={() => setShowRepo(false)}
              onPersist={async (patch) => {
                setSaving(true);
                try {
                  await persistDisposition(patch);
                  setMessage('Disposition updated.');
                } catch (e) {
                  alert(e.message || 'Could not save disposition');
                } finally {
                  setSaving(false);
                }
              }}
              onSavedDoc={async (doc_type, title, html, meta) => {
                setSaving(true);
                try {
                  await saveDoc(doc_type, title, html, meta);
                  setMessage(`${title} saved.`);
                } catch (e) {
                  alert(e.message || 'Could not save document');
                } finally {
                  setSaving(false);
                }
              }}
            />
          )}

          <div className="grid md:grid-cols-3 gap-3">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="text-sm font-medium mb-2">Buyer</h3>
              <DetailRow label="Name" value={detail.buyer_name} />
              <DetailRow label="Phone" value={detail.buyer_phone} />
              <DetailRow label="Email" value={detail.buyer_email} />
              <DetailRow
                label="Address"
                value={[
                  detail.buyer_street,
                  [detail.buyer_city, detail.buyer_state, detail.buyer_zip]
                    .filter(Boolean)
                    .join(', '),
                ]
                  .filter(Boolean)
                  .join(', ')}
              />
              <DetailRow
                label="Service contract"
                value={detail.service_contract ? 'Accepted' : 'Declined (signed)'}
              />
              <DetailRow
                label="GPS (internal)"
                value={
                  detail.gps_installed
                    ? `May be equipped${detail.gps_serial ? ` · serial (internal): ${detail.gps_serial}` : ''}`
                    : 'Not flagged'
                }
              />
            </div>
            <div className="bg-white border rounded-lg p-4">
              <h3 className="text-sm font-medium mb-2">Vehicle & money</h3>
              <DetailRow
                label="Vehicle"
                value={[detail.veh_year, detail.veh_make, detail.veh_model]
                  .filter(Boolean)
                  .join(' ')}
              />
              <DetailRow label="VIN" value={detail.veh_vin} />
              <DetailRow label="Cash price" value={money(detail.cash_price)} />
              <DetailRow label="Down" value={money(detail.down_payment)} />
              <DetailRow
                label="APR"
                value={
                  detail.apr != null
                    ? formatAprPercent(
                        Number(detail.apr) > 1 ? Number(detail.apr) : Number(detail.apr)
                      )
                    : '0%'
                }
              />
              <DetailRow label="Finance charge" value={money(detail.finance_charge)} />
              <DetailRow label="Time balance" value={money(detail.time_balance)} />
              <DetailRow label="Tax/Title/License" value={money(detail.tax_title_amt)} />
              <DetailRow label="First due" value={detail.first_due ? String(detail.first_due).slice(0, 10) : null} />
              <DetailRow label="Biweekly" value={`${money(detail.pay_amount)} x ${detail.num_payments}`} />
              <DetailRow
                label="Late events"
                value={`${detail.late_event_count ?? (detail.late_events || []).length} on file`}
              />
            </div>
            <div className="bg-white border rounded-lg p-3">
              <div className="text-xs font-medium text-neutral-500 mb-2">
                Post payment / fee (prints receipt)
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  className={inputCls}
                  placeholder="Amount"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                />
                <input
                  type="date"
                  className={inputCls}
                  value={payForm.paid_on}
                  onChange={(e) => setPayForm({ ...payForm, paid_on: e.target.value })}
                />
                <select
                  className={inputCls}
                  value={payForm.kind}
                  onChange={(e) => setPayForm({ ...payForm, kind: e.target.value })}
                >
                  <option value="installment">Installment</option>
                  <option value="tax_title">Tax/Title/License</option>
                  <option value="late_fee">Late fee</option>
                  <option value="decline_fee">Decline fee</option>
                  <option value="repo_fee">Repo fee</option>
                  <option value="impound_fee">Impound admin</option>
                  <option value="other">Other / add to bill</option>
                </select>
                <button
                  type="button"
                  disabled={saving}
                  onClick={postPayment}
                  className="px-2 py-1.5 text-sm bg-neutral-900 text-white rounded-lg"
                >
                  Post + receipt
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b text-sm font-medium">Schedule</div>
              <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500 bg-neutral-50">
                  <tr>
                    <th className="px-3 py-1.5 text-left">#</th>
                    <th className="px-3 py-1.5 text-left">Due</th>
                    <th className="px-3 py-1.5 text-left">Type</th>
                    <th className="px-3 py-1.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.schedule_json || []).map((row) => (
                    <tr key={row.num} className="border-t">
                      <td className="px-3 py-1.5">{row.num}</td>
                      <td className="px-3 py-1.5">{formatDate(parseDate(row.date))}</td>
                      <td className="px-3 py-1.5">
                        {row.kind === 'tax_title'
                          ? 'Tax/Title/License'
                          : row.label || 'Installment'}
                      </td>
                      <td className="px-3 py-1.5 text-right">{money(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b text-sm font-medium">Payments / bill lines</div>
              <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500 bg-neutral-50">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Date</th>
                    <th className="px-3 py-1.5 text-left">Kind</th>
                    <th className="px-3 py-1.5 text-right">Amount</th>
                    <th className="px-3 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.payments || []).map((p) => (
                    <tr
                      key={p.id}
                      className={`border-t ${p.voided_at ? 'opacity-40 line-through' : 'cursor-pointer hover:bg-neutral-50'}`}
                      onClick={() => !p.voided_at && reprintPaymentReceipt(p)}
                      title={p.voided_at ? 'Voided' : 'Click to reprint receipt'}
                    >
                      <td className="px-3 py-1.5">{String(p.paid_on).slice(0, 10)}</td>
                      <td className="px-3 py-1.5">{p.kind}</td>
                      <td className="px-3 py-1.5 text-right">{money(p.amount)}</td>
                      <td className="px-3 py-1.5 text-right">
                        {!p.voided_at && (
                          <span className="inline-flex gap-2">
                            <button
                              type="button"
                              className="text-xs text-blue-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                reprintPaymentReceipt(p);
                              }}
                            >
                              Receipt
                            </button>
                            <button
                              type="button"
                              className="text-xs text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                voidPayment(p.id);
                              }}
                            >
                              Void
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!(detail.payments || []).length && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-neutral-400">
                        No payments posted
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium">
                Late history ({detail.late_event_count ?? (detail.late_events || []).length})
              </h3>
            </div>
            <div className="grid sm:grid-cols-4 gap-2">
              <input
                type="date"
                className={inputCls}
                value={lateForm.event_on}
                onChange={(e) => setLateForm({ ...lateForm, event_on: e.target.value })}
              />
              <select
                className={inputCls}
                value={lateForm.reason}
                onChange={(e) => setLateForm({ ...lateForm, reason: e.target.value })}
              >
                {LATE_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <select
                className={inputCls}
                value={lateForm.solution}
                onChange={(e) => setLateForm({ ...lateForm, solution: e.target.value })}
              >
                {LATE_SOLUTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={saving}
                onClick={postLateEvent}
                className="px-2 py-1.5 text-sm bg-neutral-900 text-white rounded-lg"
              >
                Log late event
              </button>
              <input
                className={`${inputCls} sm:col-span-4`}
                placeholder="Notes / what happened"
                value={lateForm.notes}
                onChange={(e) => setLateForm({ ...lateForm, notes: e.target.value })}
              />
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500 bg-neutral-50">
                <tr>
                  <th className="px-3 py-1.5 text-left">Date</th>
                  <th className="px-3 py-1.5 text-left">Reason</th>
                  <th className="px-3 py-1.5 text-left">Solution</th>
                  <th className="px-3 py-1.5 text-left">Notes</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {(detail.late_events || []).map((ev) => (
                  <tr key={ev.id} className="border-t">
                    <td className="px-3 py-1.5">{String(ev.event_on).slice(0, 10)}</td>
                    <td className="px-3 py-1.5">
                      {LATE_REASONS.find((r) => r.value === ev.reason)?.label || ev.reason}
                    </td>
                    <td className="px-3 py-1.5">
                      {LATE_SOLUTIONS.find((r) => r.value === ev.solution)?.label ||
                        ev.solution ||
                        '-'}
                    </td>
                    <td className="px-3 py-1.5 text-neutral-600">{ev.notes || '-'}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() => removeLateEvent(ev.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {!(detail.late_events || []).length && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-neutral-400">
                      No late events logged
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {(detail.documents || []).length > 0 && (
            <div className="bg-white border rounded-lg p-3">
              <h3 className="text-sm font-medium mb-2">Saved documents</h3>
              <ul className="text-sm space-y-1">
                {detail.documents.map((d) => (
                  <li key={d.id} className="flex justify-between gap-2">
                    <span>
                      {d.title || d.doc_type}{' '}
                      <span className="text-neutral-400 text-xs">
                        {d.created_at ? String(d.created_at).slice(0, 19) : ''}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="text-xs text-sky-700"
                      onClick={() => {
                        const w = window.open('', '_blank');
                        if (w) {
                          w.document.write(d.html || '');
                          w.document.close();
                        }
                      }}
                    >
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {(tab === 'new' || tab === 'edit') && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {tab === 'edit' && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <button
                  type="button"
                  className="text-neutral-600 hover:underline"
                  onClick={() => openAccount(editingId)}
                >
                  ← Back to details
                </button>
                <span className="font-medium">Editing account #{editingId}</span>
              </div>
            )}

            <section className="bg-white border rounded-lg p-4 space-y-3">
              <h3 className="font-medium">Buyer</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name *">
                  <input className={inputCls} value={form.buyerName} onChange={set('buyerName')} />
                </Field>
                <Field label="DOB">
                  <input type="date" className={inputCls} value={form.buyerDob} onChange={set('buyerDob')} />
                </Field>
                <Field label="Street" className="sm:col-span-2">
                  <input className={inputCls} value={form.buyerStreet} onChange={set('buyerStreet')} />
                </Field>
                <Field label="City">
                  <input className={inputCls} value={form.buyerCity} onChange={set('buyerCity')} />
                </Field>
                <Field label="State">
                  <input className={inputCls} value={form.buyerState} onChange={set('buyerState')} />
                </Field>
                <Field label="ZIP">
                  <input className={inputCls} value={form.buyerZip} onChange={set('buyerZip')} />
                </Field>
                <Field label="Phone">
                  <input className={inputCls} value={form.buyerPhone} onChange={set('buyerPhone')} />
                </Field>
                <Field label="Email">
                  <input className={inputCls} value={form.buyerEmail} onChange={set('buyerEmail')} />
                </Field>
                <Field label="Co-buyer">
                  <input className={inputCls} value={form.coBuyerName} onChange={set('coBuyerName')} />
                </Field>
              </div>
            </section>

            <section className="bg-white border rounded-lg p-4 space-y-3">
              <h3 className="font-medium">Vehicle &amp; our position</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Year">
                  <input className={inputCls} value={form.vehYear} onChange={set('vehYear')} />
                </Field>
                <Field label="Make">
                  <input className={inputCls} value={form.vehMake} onChange={set('vehMake')} />
                </Field>
                <Field label="Model">
                  <input className={inputCls} value={form.vehModel} onChange={set('vehModel')} />
                </Field>
                <Field label="VIN *">
                  <input className={inputCls} value={form.vehVin} onChange={set('vehVin')} />
                </Field>
                <Field label="Stock #">
                  <input className={inputCls} value={form.vehStock} onChange={set('vehStock')} />
                </Field>
                <Field label="Sale date">
                  <input type="date" className={inputCls} value={form.saleDate} onChange={set('saleDate')} />
                </Field>
                <Field label="Cost in car (editable)" className="sm:col-span-2">
                  <input className={inputCls} value={form.costInCar} onChange={set('costInCar')} />
                </Field>
                <label className="flex items-end gap-2 pb-2 text-sm">
                  <input type="checkbox" checked={form.gpsInstalled} onChange={set('gpsInstalled')} />
                  May have GPS (internal)
                </label>
                {form.gpsInstalled && (
                  <Field label="GPS serial (INTERNAL only - never on customer forms)" className="sm:col-span-2">
                    <input
                      className={inputCls}
                      value={form.gpsSerial}
                      onChange={set('gpsSerial')}
                      placeholder="Internal desk use only"
                    />
                  </Field>
                )}
              </div>
            </section>

            <section className="bg-white border rounded-lg p-4 space-y-3">
              <h3 className="font-medium">Money &amp; schedule</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Cash price">
                  <input className={inputCls} value={form.cashPrice} onChange={set('cashPrice')} />
                </Field>
                <Field label="Down payment">
                  <input className={inputCls} value={form.downPayment} onChange={set('downPayment')} />
                </Field>
                <Field label="Trade amount">
                  <input className={inputCls} value={form.tradeAmt} onChange={set('tradeAmt')} />
                </Field>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <React.Fragment key={i}>
                    <Field label={`Fee ${i} label`} className="sm:col-span-2">
                      <input
                        className={inputCls}
                        value={form[`fee${i}Label`]}
                        onChange={set(`fee${i}Label`)}
                      />
                    </Field>
                    <Field label="Amount">
                      <input
                        className={inputCls}
                        value={form[`fee${i}Amt`]}
                        onChange={set(`fee${i}Amt`)}
                      />
                    </Field>
                  </React.Fragment>
                ))}
                <label className="flex items-center gap-2 text-sm sm:col-span-3">
                  <input
                    type="checkbox"
                    checked={form.serviceContract}
                    onChange={set('serviceContract')}
                  />
                  Service contract (${form.serviceContractFee || DEFAULT_SERVICE_CONTRACT_FEE}) - if off,
                  customer signs decline
                </label>
                <Field label="Tax/Title/License amt">
                  <input className={inputCls} value={form.taxTitleAmt} onChange={set('taxTitleAmt')} />
                </Field>
                <Field label="First due (+2 weeks from sale)">
                  <input type="date" className={inputCls} value={form.firstDue} onChange={set('firstDue')} />
                </Field>
                <Field label="Tax/Title/License due (+2 weeks after first)">
                  <input
                    type="date"
                    className={inputCls}
                    value={form.taxTitleDate}
                    onChange={set('taxTitleDate')}
                  />
                </Field>
                <Field label={`APR % (Ohio max ${OHIO_MAX_APR * 100}%)`}>
                  <input
                    className={inputCls}
                    value={form.apr}
                    onChange={set('apr')}
                    placeholder="18"
                  />
                </Field>
                <Field label="# biweekly installments">
                  <input className={inputCls} value={form.numPayments} onChange={set('numPayments')} />
                </Field>
                <Field label="Biweekly payment (auto from APR + #)">
                  <input
                    className={inputCls}
                    value={form.payAmount}
                    onChange={set('payAmount')}
                    placeholder="Computed when APR + # set"
                  />
                </Field>
                <Field label="Late fee $">
                  <input className={inputCls} value={form.lateFee} onChange={set('lateFee')} />
                </Field>
                <Field label="Late starts (days)">
                  <input
                    className={inputCls}
                    value={form.lateFeeStartDays}
                    onChange={set('lateFeeStartDays')}
                  />
                </Field>
                <Field label="Late every (days)">
                  <input
                    className={inputCls}
                    value={form.lateFeeRepeatDays}
                    onChange={set('lateFeeRepeatDays')}
                  />
                </Field>
                <Field label="Decline fee $">
                  <input className={inputCls} value={form.declineFee} onChange={set('declineFee')} />
                </Field>
                <Field label="Impound admin $">
                  <input className={inputCls} value={form.impoundFee} onChange={set('impoundFee')} />
                </Field>
              </div>
            </section>

            <section className="bg-white border rounded-lg p-4 space-y-3">
              <h3 className="font-medium">Card (last-4 only)</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Name on card">
                  <input className={inputCls} value={form.cardName} onChange={set('cardName')} />
                </Field>
                <Field label="Brand">
                  <input className={inputCls} value={form.cardBrand} onChange={set('cardBrand')} />
                </Field>
                <Field label="Last 4">
                  <input
                    className={inputCls}
                    maxLength={4}
                    value={form.cardLast4}
                    onChange={set('cardLast4')}
                  />
                </Field>
              </div>
            </section>
          </div>

          <div className="space-y-3">
            <div className="bg-white border rounded-lg p-4 sticky top-4">
              <h3 className="font-medium mb-3">Customer payment stack</h3>
              <dl className="text-sm space-y-1.5">
                <div className="flex justify-between">
                  <dt className="text-neutral-500">Total</dt>
                  <dd>{money(calc.cashPriceTotal)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-neutral-500">Minus down + trade</dt>
                  <dd>-{money(calc.downAndTrade)}</dd>
                </div>
                <div className="flex justify-between font-medium">
                  <dt>Amount financed</dt>
                  <dd>{money(calc.unpaidBalance)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-neutral-500">APR (ORC 1317.061)</dt>
                  <dd>{calc.aprDisplay || formatAprPercent(form.apr)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-neutral-500">Finance charge</dt>
                  <dd>{money(calc.financeCharge)}</dd>
                </div>
                <div className="flex justify-between font-medium">
                  <dt>Time balance</dt>
                  <dd>{money(calc.timeBalance)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-neutral-500">One payment (first)</dt>
                  <dd>
                    {money(calc.paymentAmt)} · {formatDate(calc.firstDueDate)}
                  </dd>
                </div>
                {calc.warning && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                    {calc.warning}
                  </p>
                )}
                <div className="flex justify-between">
                  <dt className="text-neutral-500">Tax/Title/License</dt>
                  <dd>
                    {money(calc.taxTitle)} · {formatDate(parseDate(calc.taxTitleDate))}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-neutral-500">After first payment</dt>
                  <dd>{money(calc.remainingAfterFirst)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-neutral-500">Installments</dt>
                  <dd>{calc.nPay}</dd>
                </div>
                {form.serviceContract && (
                  <div className="flex justify-between text-sky-800">
                    <dt>Service contract</dt>
                    <dd>{money(calc.serviceFee)}</dd>
                  </div>
                )}
              </dl>
              {calc.warning && <p className="text-xs text-amber-700 mt-2">{calc.warning}</p>}
              <p className="text-[11px] text-neutral-500 mt-2">
                Tax/title week skips a regular installment so the customer is not stacked.
              </p>
              <div className="flex flex-col gap-2 mt-4">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveAccount}
                  className="w-full py-2 bg-neutral-900 text-white rounded-lg text-sm font-medium"
                >
                  {saving
                    ? 'Saving...'
                    : editingId
                      ? `Save changes #${editingId}`
                      : 'Save account'}
                </button>
                <button
                  type="button"
                  onClick={() => printBhphPackage(company, form)}
                  className="w-full py-2 border rounded-lg text-sm"
                >
                  Preview / print package
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'settings' && (
        <div className="bg-white border rounded-lg p-4 max-w-3xl space-y-3">
          <h3 className="font-medium">Seller / fees / repo company</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ['legal_name', 'Legal name'],
              ['dba', 'DBA'],
              ['street', 'Street'],
              ['city', 'City'],
              ['state', 'State'],
              ['zip', 'ZIP'],
              ['phone', 'Phone'],
              ['email', 'Email'],
              ['alert_email', 'Decline alert email'],
              ['county', 'County (venue)'],
              ['late_fee', 'Late fee $'],
              ['late_fee_start_days', 'Late starts (days)'],
              ['late_fee_repeat_days', 'Late every (days)'],
              ['decline_fee', 'Decline fee $'],
              ['repo_fee', 'Repo fee disclosure $'],
              ['default_repo_expense', 'Default actual repo expense $ (e.g. 640)'],
              ['storage_per_day', 'Storage $ per day (e.g. 20)'],
              ['impound_admin_fee', 'Impound admin $'],
              ['key_turnin_bonus', 'Key turn-in $'],
              ['service_contract_fee', 'Service contract $'],
              ['default_apr', `Default APR % (max ${OHIO_MAX_APR * 100})`],
              ['repo_company_name', 'Repo company name'],
              ['repo_company_phone', 'Repo company phone'],
              ['repo_company_address', 'Repo company address'],
            ].map(([key, label]) => (
              <Field key={key} label={label}>
                <input
                  className={inputCls}
                  value={companyForm[key] ?? ''}
                  onChange={(e) => setCompanyForm({ ...companyForm, [key]: e.target.value })}
                />
              </Field>
            ))}
          </div>
          <p className="text-xs text-neutral-500">
            Default APR applies to new BHPH accounts. Ohio ORC 1317.061 caps APR at 25%. Finance charge is
            computed as a true biweekly amortizing APR (not add-on). Have Ohio counsel review contracts before
            customer use.
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={saveCompany}
            className="px-4 py-2 bg-neutral-900 text-white rounded-lg text-sm"
          >
            Save settings
          </button>
        </div>
      )}
    </div>
  );
}
